import { useInfiniteQuery, useQuery } from "@tanstack/react-query";
import {
  ChevronRight,
  Loader2,
  Search,
  SlidersHorizontal,
  X,
} from "lucide-react";
import React, { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";

import {
  listReceivingOrders,
  listReceivingOrderStates,
  listReceivingOrganizations,
  type GoodsStatus,
  type SupplierOrderCard,
} from "@/api/receiving";
import { GOODS_FILTER_OPTIONS, GoodsBadge } from "@/pages/receiving/goods";
import { DateRangePicker } from "@/components/DateRangePicker";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { localISODate } from "@/lib/date-range";
import { fmtDate } from "@/lib/format";
import { useDebounced } from "@/lib/use-debounced";
import { cn } from "@/lib/utils";

/**
 * Приёмка поставок — список заказов поставщику (mobile-first).
 *
 * Окно по дате ПОСТУПЛЕНИЯ, при первом заходе — сегодня («что приедет
 * сегодня»); фильтры организация/состояние, поиск по значащему номеру (поиск
 * снимает окно дат — ищут и вчерашние заказы).
 *
 * Даты СОХРАНЯЮТСЯ между заходами: приёмщик держит своё окно (неделю, три дня)
 * и переставлял его после каждого обновления страницы. Риск при этом реальный —
 * протухшее окно прячет сегодняшние машины, — поэтому он не молчаливый: окно,
 * не включающее сегодня, помечено прямо над списком и сбрасывается одним тапом.
 * Прятать машины МОЖНО, делать это незаметно — нельзя.
 */
const LS = {
  q: "wh_rcv_q",
  org: "wh_rcv_org",
  state: "wh_rcv_state",
  receipt: "wh_rcv_receipt",
  from: "wh_rcv_from",
  to: "wh_rcv_to",
};
const lsGet = (k: string) => {
  try {
    return localStorage.getItem(k) ?? "";
  } catch {
    return "";
  }
};

export function ReceivingListPage(): React.ReactElement {
  // Раздел один: «Приёмка (новая)» слита с основной. Параметра `basePath`
  // больше нет — он существовал ровно для времени обкатки двух разделов.
  const basePath = "/receiving";
  const navigate = useNavigate();
  const [rawQuery, setRawQuery] = useState(() => lsGet(LS.q));
  const query = useDebounced(rawQuery.trim(), 350);
  const [filtersOpen, setFiltersOpen] = useState(false);
  const [orgRef, setOrgRef] = useState<string>(() => lsGet(LS.org));
  const [stateRef, setStateRef] = useState<string>(() => lsGet(LS.state));
  const [receipt, setReceipt] = useState<GoodsStatus | "">(
    () => lsGet(LS.receipt) as GoodsStatus | "",
  );
  // Из хранилища берём только то, что похоже на дату: мусор в localStorage
  // ушёл бы в запрос и вернулся невнятной 422 вместо списка.
  const savedDate = (k: string) => (/^\d{4}-\d{2}-\d{2}$/.test(lsGet(k)) ? lsGet(k) : "");
  const [dateFrom, setDateFrom] = useState<string>(
    () => savedDate(LS.from) || localISODate(),
  );
  const [dateTo, setDateTo] = useState<string>(() => savedDate(LS.to) || localISODate());

  useEffect(() => {
    try {
      localStorage.setItem(LS.q, rawQuery);
      localStorage.setItem(LS.org, orgRef);
      localStorage.setItem(LS.state, stateRef);
      localStorage.setItem(LS.receipt, receipt);
      localStorage.setItem(LS.from, dateFrom);
      localStorage.setItem(LS.to, dateTo);
    } catch {
      /* приватный режим — не критично */
    }
  }, [rawQuery, orgRef, stateRef, receipt, dateFrom, dateTo]);

  const orgsQ = useQuery({
    queryKey: ["receiving-orgs"],
    queryFn: () => listReceivingOrganizations(),
    enabled: filtersOpen,
    staleTime: 5 * 60_000,
  });
  const statesQ = useQuery({
    queryKey: ["receiving-states"],
    queryFn: () => listReceivingOrderStates(),
    enabled: filtersOpen,
    staleTime: 5 * 60_000,
  });

  const {
    data,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
    isLoading,
    isError,
    error,
  } = useInfiniteQuery({
    queryKey: [
      "receiving-orders", query, orgRef, stateRef, receipt, dateFrom, dateTo,
    ],
    queryFn: ({ pageParam }) =>
      listReceivingOrders({
        q: query,
        organization_ref: orgRef,
        state_ref: stateRef,
        receipt,
        date_from: dateFrom,
        date_to: dateTo,
        skip: pageParam,
      }),
    initialPageParam: 0,
    getNextPageParam: (lastPage, allPages) =>
      lastPage.has_more
        ? allPages.reduce((n, p) => n + p.items.length, 0)
        : undefined,
  });

  const orders = data?.pages.flatMap((p) => p.items) ?? [];
  const activeFilters =
    (orgRef ? 1 : 0) + (stateRef ? 1 : 0) + (receipt ? 1 : 0);
  const isSearching = Boolean(query);

  // Бесконечная подгрузка: следующая страница, когда виден сентинел.
  const sentinelRef = useRef<HTMLDivElement | null>(null);
  useEffect(() => {
    const el = sentinelRef.current;
    if (!el) return;
    const io = new IntersectionObserver((entries) => {
      if (entries[0]?.isIntersecting && hasNextPage && !isFetchingNextPage) {
        fetchNextPage();
      }
    });
    io.observe(el);
    return () => io.disconnect();
  }, [hasNextPage, isFetchingNextPage, fetchNextPage]);

  const rangeIs = (from: string, to: string) => dateFrom === from && dateTo === to;
  const today = localISODate();
  // Окно сохраняется между заходами, поэтому назавтра оно может уже не
  // включать сегодня. Поиск окно снимает — при непустом запросе молчим.
  const staleWindow = !query && (dateFrom > today || dateTo < today);
  const presets: { label: string; from: string; to: string }[] = [
    { label: "Сегодня", from: localISODate(), to: localISODate() },
    { label: "Завтра", from: localISODate(1), to: localISODate(1) },
    { label: "3 дня", from: localISODate(), to: localISODate(2) },
  ];

  return (
    <div className="mx-auto max-w-xl space-y-3">
      <h1 className="text-xl font-semibold">Приёмка поставок</h1>

      <div className="flex items-center gap-2">
        <div className="relative flex-1">
          <Search className="pointer-events-none absolute left-2 top-1/2 size-4 -translate-y-1/2 text-muted-foreground" />
          {/* ⚠️ Клавиатура ОБЫЧНАЯ, без `inputMode="numeric"`. Он остался с тех
              пор, когда поле искало только номер: на телефоне это открывает
              цифровую панель, и буквы ввести нельзя ВООБЩЕ — поиск по
              поставщику недоступен, хотя подпись его обещает (боевой отчёт
              склада 07.09.2026). */}
          <Input
            value={rawQuery}
            onChange={(e) => setRawQuery(e.target.value)}
            placeholder="Номер заказа или поставщик"
            className="pl-8"
          />
          {rawQuery && (
            <button
              type="button"
              onClick={() => setRawQuery("")}
              className="absolute right-2 top-1/2 -translate-y-1/2 text-muted-foreground"
              aria-label="Очистить"
            >
              <X className="size-4" />
            </button>
          )}
        </div>
        <Button
          variant={activeFilters ? "default" : "outline"}
          size="icon"
          onClick={() => setFiltersOpen((s) => !s)}
          aria-label="Фильтры"
          className="relative shrink-0"
        >
          <SlidersHorizontal className="size-4" />
          {activeFilters > 0 && (
            <span className="absolute -right-1 -top-1 flex size-4 items-center justify-center rounded-full bg-primary text-[10px] text-primary-foreground">
              {activeFilters}
            </span>
          )}
        </Button>
      </div>

      {/* Окно по дате поступления — всегда на виду: это главный фильтр экрана. */}
      <Card className="space-y-2 p-3">
        <div className="flex flex-wrap items-center gap-1.5">
          <span className="text-xs font-medium text-muted-foreground">Дата приёмки</span>
          {presets.map((pr) => (
            <Chip
              key={pr.label}
              active={rangeIs(pr.from, pr.to)}
              onClick={() => {
                setDateFrom(pr.from);
                setDateTo(pr.to);
              }}
            >
              {pr.label}
            </Chip>
          ))}
        </div>
        <DateRangePicker
          from={dateFrom}
          to={dateTo}
          onChange={(f, t) => {
            setDateFrom(f);
            setDateTo(t);
          }}
        />
        {staleWindow && (
          <div className="flex items-center gap-2 rounded-md bg-amber-100 px-2 py-1.5 text-[11px] text-amber-900">
            <span className="flex-1">
              Окно не включает сегодня — машины сегодняшнего дня в списке не видны.
            </span>
            <button
              type="button"
              className="shrink-0 rounded bg-amber-900 px-2 py-1 font-medium text-amber-50"
              onClick={() => {
                setDateFrom(today);
                setDateTo(today);
              }}
            >
              Сегодня
            </button>
          </div>
        )}
        {isSearching && (
          <p className="text-[11px] text-muted-foreground">
            Ищем и по номеру, и по названию поставщика (слова — по отдельности
            и в любом порядке), свежие сверху. Окно дат не применяется: по
            номеру — за всё время, по поставщику — за последний год.
          </p>
        )}
      </Card>

      {filtersOpen && (
        <Card className="space-y-3 p-3">
          <div>
            <div className="mb-1 text-xs font-medium text-muted-foreground">
              Приёмка товара (по 1С)
            </div>
            <div className="flex flex-wrap gap-1.5">
              {GOODS_FILTER_OPTIONS.map((o) => (
                <Chip
                  key={o.value || "all"}
                  active={receipt === o.value}
                  onClick={() => setReceipt(o.value)}
                >
                  {o.label}
                </Chip>
              ))}
            </div>
          </div>
          <div>
            <div className="mb-1 text-xs font-medium text-muted-foreground">
              Организация
            </div>
            <div className="flex flex-wrap gap-1.5">
              {orgsQ.isLoading && (
                <Loader2 className="size-4 animate-spin text-muted-foreground" />
              )}
              {orgsQ.data?.map((o) => (
                <Chip
                  key={o.ref}
                  active={orgRef === o.ref}
                  onClick={() => setOrgRef(orgRef === o.ref ? "" : o.ref)}
                >
                  {o.name}
                </Chip>
              ))}
            </div>
          </div>
          <div>
            <div className="mb-1 text-xs font-medium text-muted-foreground">
              Состояние заказа
            </div>
            <div className="flex flex-wrap gap-1.5">
              {statesQ.isLoading && (
                <Loader2 className="size-4 animate-spin text-muted-foreground" />
              )}
              {statesQ.data?.map((s) => (
                <Chip
                  key={s.ref}
                  active={stateRef === s.ref}
                  onClick={() => setStateRef(stateRef === s.ref ? "" : s.ref)}
                >
                  {s.name}
                </Chip>
              ))}
            </div>
          </div>
          {activeFilters > 0 && (
            <Button
              variant="ghost"
              size="sm"
              onClick={() => {
                setOrgRef("");
                setStateRef("");
                setReceipt("");
              }}
            >
              Сбросить фильтры
            </Button>
          )}
        </Card>
      )}

      {isLoading && (
        <div className="flex justify-center py-10 text-muted-foreground">
          <Loader2 className="size-6 animate-spin" />
        </div>
      )}
      {isError && (
        <Card className="border-destructive/50 p-3 text-sm text-destructive">
          Не удалось загрузить заказы: {(error as Error)?.message ?? "ошибка"}
        </Card>
      )}
      {!isLoading && !isError && orders.length === 0 && (
        <p className="py-10 text-center text-sm text-muted-foreground">
          {query
            ? `Заказ № ${query} не найден`
            : "Нет заказов поставщику с приёмкой в выбранные даты"}
        </p>
      )}

      <div className="space-y-2">
        {orders.map((o) => (
          <OrderCardView
            key={o.ref}
            order={o}
            onClick={() => navigate(`${basePath}/orders/${o.ref}`)}
          />
        ))}
      </div>

      <div ref={sentinelRef} className="h-6" />
      {isFetchingNextPage && (
        <div className="flex justify-center py-2 text-muted-foreground">
          <Loader2 className="size-5 animate-spin" />
        </div>
      )}
    </div>
  );
}

function Chip({
  active,
  onClick,
  children,
}: {
  active: boolean;
  onClick: () => void;
  children: React.ReactNode;
}): React.ReactElement {
  return (
    <button
      type="button"
      onClick={onClick}
      className={cn(
        "rounded-full border px-3 py-1 text-xs transition-colors",
        active
          ? "border-primary bg-primary text-primary-foreground"
          : "border-input bg-background text-foreground hover:bg-accent",
      )}
    >
      {children}
    </button>
  );
}

/** Статус фотофиксации. Префикс «Фото:» обязателен: рядом бейдж «Товар: …»
 *  (приёмка по 1С), без префикса оба читались как «принят/не принят». */
function ReceivingBadge({
  status,
}: {
  status: SupplierOrderCard["receiving_status"];
}): React.ReactElement | null {
  if (status === "completed") {
    return (
      <span className="rounded bg-emerald-100 px-1.5 py-0.5 text-[10px] font-medium text-emerald-700">
        Фото: ✓ в 1С
      </span>
    );
  }
  if (status === "in_progress") {
    return (
      <span className="rounded bg-amber-100 px-1.5 py-0.5 text-[10px] font-medium text-amber-700">
        Фото: в работе
      </span>
    );
  }
  return null;
}

function OrderCardView({
  order,
  onClick,
}: {
  order: SupplierOrderCard;
  onClick: () => void;
}): React.ReactElement {
  return (
    <Card className="transition-colors hover:bg-accent">
      <button
        type="button"
        onClick={onClick}
        className="flex w-full items-center gap-2 p-3 text-left"
      >
        <div className="min-w-0 flex-1 space-y-1">
          <div className="flex flex-wrap items-center gap-2">
            <span className="font-semibold">{order.number}</span>
            {/* Дата ПОСТУПЛЕНИЯ, а не дата документа: заказ мог быть заведён
                месяц назад, а машину ждут в конкретный день. */}
            <span className="text-xs text-muted-foreground">
              {order.receiving_date
                ? `приход ${fmtDate(order.receiving_date)}`
                : fmtDate(order.date)}
            </span>
            {order.state_name && (
              <span className="rounded bg-muted px-1.5 py-0.5 text-[10px] text-muted-foreground">
                {order.state_name}
              </span>
            )}
            <GoodsBadge goods={order.goods} />
            <ReceivingBadge status={order.receiving_status} />
          </div>
          <div className="truncate text-sm">{order.organization_name}</div>
          <div className="truncate text-sm text-muted-foreground">
            ← {order.kontragent_name}
          </div>
        </div>
        <ChevronRight className="size-5 shrink-0 text-muted-foreground" />
      </button>
    </Card>
  );
}
